import { PLANNER_API_URL } from "../../config/api";
import { AuthSession } from "./AuthSession";
import type { AuthRecord, AuthUser, PlannerAuthResponse } from "./AuthTypes";

function plannerUrl(path: string): string {
  const base = PLANNER_API_URL.replace(/\/+$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalized}`;
}

async function readJson(response: Response): Promise<unknown> {
  return response.json().catch(() => ({}));
}

export async function plannerLogin(
  identifier: string,
  password: string,
  recaptchaToken?: string,
): Promise<PlannerAuthResponse> {
  const response = await fetch(plannerUrl("/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier, password, recaptchaToken }),
  });
  const data = (await readJson(response)) as PlannerAuthResponse & {
    message?: string;
    code?: string;
  };
  if (!response.ok) {
    return {
      status: "ERROR",
      errorCode: data.errorCode || data.code || "LOGIN_FAILED",
    };
  }
  return data;
}

export async function plannerSelectCompany(
  selectionToken: string,
  companyId: string,
): Promise<PlannerAuthResponse> {
  const response = await fetch(plannerUrl("/auth/company"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ selectionToken, companyId }),
  });
  const data = (await readJson(response)) as PlannerAuthResponse & {
    code?: string;
  };
  if (!response.ok) {
    return {
      status: "ERROR",
      errorCode: data.errorCode || data.code || "LOGIN_FAILED",
    };
  }
  return data;
}

export function storeAuthResponse(data: PlannerAuthResponse): AuthRecord | null {
  if (data.status !== "AUTHENTICATED" || !data.ticket) return null;
  const record: AuthRecord = {
    ticket: data.ticket,
    expiresAt: data.expiresAt,
    user: data.user,
  };
  AuthSession.set(record);
  return record;
}

export async function plannerMe(): Promise<AuthUser | null> {
  const record = AuthSession.get();
  if (!record?.ticket) return null;
  const response = await fetch(plannerUrl("/auth/me"), {
    headers: { Authorization: `Bearer ${record.ticket}` },
  });
  if (!response.ok) {
    if (response.status === 401) AuthSession.clear();
    return null;
  }
  const user = (await readJson(response)) as AuthUser;
  AuthSession.set({ ...record, user });
  return user;
}

export async function plannerLogout(): Promise<void> {
  const record = AuthSession.get();
  try {
    if (record?.ticket) {
      await fetch(plannerUrl("/auth/logout"), {
        method: "POST",
        headers: { Authorization: `Bearer ${record.ticket}` },
      });
    }
  } finally {
    AuthSession.clear();
  }
}

export { plannerUrl };
